import React, { forwardRef } from "react";
import { cn } from "../../lib/utils.js";

/* Wordmark — the product's logotype as a design-system part (app light
   path, task 5). The artwork is the brand guide's own vector, the same
   path data as the Keycloak login theme's `brandLogo` macro
   (infrastructure/local/keycloak/themes/wikiflow/login/template.ftl), so the
   sign-in page and the app draw one mark.

   It fills with currentColor: the surrounding text colour sets it per theme,
   and Windows high-contrast mode recolours it like text. Data-blind — this
   package never names the product, so the caller passes the name as `label`
   (the shell passes `PRODUCT_NAME` from @playbookai/types). */

/** Height steps 16/20/24/32px; the width follows from the artwork's aspect
 *  ratio (1813 × 240). `md` (24px, about 181px wide) is the login page's
 *  mark. */
export type WordmarkSize = "xs" | "sm" | "md" | "lg";

const SIZE: Record<WordmarkSize, string> = {
  xs: "h-4",
  sm: "h-5",
  md: "h-6",
  lg: "h-8",
};

/* One path per glyph, left to right. The first is the K the shell's
   favicon (apps/shell/public/favicon.svg) is drawn from. */
const GLYPHS = [
  "M 238.410156 331.179688 L 120.128906 482.023438 L 120.128906 331.179688 L 54.328125 331.179688 L 54.328125 570.941406 L 114.054688 570.941406 L 184.808594 483.664062 L 236.492188 570.941406 L 311.617188 570.941406 L 226.828125 431.828125 L 308.421875 331.179688 Z M 238.410156 331.179688",
  "M 432.136719 331.179688 L 323.125 570.941406 L 393.777344 570.941406 L 413.734375 524.90625 L 537.882812 524.90625 L 557.773438 570.941406 L 628.421875 570.941406 L 519.410156 331.179688 Z M 475.917969 381.472656 L 514.820312 471.519531 L 436.875 471.519531 Z M 475.917969 381.472656",
  "M 648.3125 570.941406 L 714.484375 570.941406 L 714.484375 331.179688 L 648.3125 331.179688 Z M 648.3125 570.941406",
  "M 968.238281 331.179688 L 889.867188 509.34375 L 810.953125 331.179688 L 739.984375 331.179688 L 851.554688 570.941406 L 926.679688 570.941406 L 1038.246094 331.179688 Z M 968.238281 331.179688",
  "M 1058.269531 331.179688 L 1076.484375 451.058594 L 1058.269531 570.941406 L 1281.085938 570.941406 L 1281.085938 517.234375 L 1129.335938 517.234375 L 1136.203125 475.035156 L 1270.53125 475.035156 L 1270.53125 427.085938 L 1136.203125 427.085938 L 1129.335938 384.886719 L 1279.488281 384.886719 L 1279.488281 331.179688 Z M 1058.269531 331.179688",
  "M 1376.265625 380.410156 L 1443.089844 380.410156 C 1453.734375 380.410156 1462.054688 382.808594 1468.019531 387.601562 C 1473.980469 392.398438 1476.972656 399.273438 1476.972656 408.222656 C 1476.972656 416.964844 1473.980469 423.78125 1468.019531 428.683594 C 1462.054688 433.589844 1453.734375 436.035156 1443.089844 436.035156 L 1376.265625 436.035156 Z M 1500.3125 475.035156 C 1513.941406 468.644531 1524.605469 459.691406 1532.28125 448.183594 C 1539.953125 436.675781 1543.785156 423.359375 1543.785156 408.222656 C 1543.785156 392.667969 1539.953125 379.1875 1532.28125 367.78125 C 1524.605469 356.382812 1513.941406 347.433594 1500.3125 340.929688 C 1486.664062 334.429688 1470.683594 331.179688 1452.351562 331.179688 L 1310.097656 331.179688 L 1310.097656 570.941406 L 1376.265625 570.941406 L 1376.265625 484.628906 L 1410.945312 484.628906 L 1475.375 570.941406 L 1552.09375 570.941406 L 1483.214844 481.066406 C 1489.273438 479.542969 1494.976562 477.539062 1500.3125 475.035156",
  "M 1675.441406 471.519531 L 1714.476562 381.472656 L 1753.378906 471.519531 Z M 1796.335938 570.941406 L 1866.984375 570.941406 L 1757.976562 331.179688 L 1670.703125 331.179688 L 1561.6875 570.941406 L 1632.332031 570.941406 L 1652.296875 524.90625 L 1776.445312 524.90625 Z M 1796.335938 570.941406",
] as const;

interface WordmarkBaseProps extends Omit<
  React.SVGAttributes<SVGSVGElement>,
  | "role"
  | "aria-hidden"
  | "aria-label"
  | "aria-labelledby"
  | "children"
  | "viewBox"
  | "fill"
> {
  size?: WordmarkSize | undefined;
}

/** The mark stands for the product: `label` is its accessible name. */
export interface WordmarkLabelledProps extends WordmarkBaseProps {
  /** The product name, passed by the caller (this package never names it). */
  label: string;
  "aria-hidden"?: undefined;
}

/** The mark is decoration beside text that already names the product. */
export interface WordmarkDecorativeProps extends WordmarkBaseProps {
  label?: undefined;
  "aria-hidden": true;
}

/** A meaningful mark must be named: `label`, or `aria-hidden` to say it is
 *  decorative. `className` carries layout only (margin, self-alignment). */
export type WordmarkProps = WordmarkLabelledProps | WordmarkDecorativeProps;

export const Wordmark = forwardRef<SVGSVGElement, WordmarkProps>(
  function Wordmark(
    { size = "md", label, "aria-hidden": ariaHidden, className, ...rest },
    ref,
  ) {
    return (
      <svg
        ref={ref}
        viewBox="54 331 1813 240"
        fill="currentColor"
        focusable="false"
        className={cn(
          "block w-auto shrink-0 aspect-[1813/240]",
          SIZE[size],
          className,
        )}
        {...rest}
        role={ariaHidden ? undefined : "img"}
        aria-label={ariaHidden ? undefined : label}
        aria-hidden={ariaHidden ? true : undefined}
      >
        {GLYPHS.map((d) => (
          <path key={d} d={d} />
        ))}
      </svg>
    );
  },
);
